import React, { useState, useEffect } from 'react';
import { taxApi } from '../services/api';
import { Card, Button, Input, Select, Modal, Badge, Spinner } from '../components/UI';
import { Plus, Trash, PencilSimple, Receipt, Check, Percent } from '@phosphor-icons/react';

export default function TaxSettingsPage() {
  const [rates, setRates] = useState([]);
  const [trn, setTrn] = useState('');
  const [trnEdit, setTrnEdit] = useState('');
  const [trnSaving, setTrnSaving] = useState(false);
  const [trnSuccess, setTrnSuccess] = useState('');
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ tax_name: '', percentage: '', tax_type: 'VAT', country: 'UAE', is_default: false });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [ratesRes, trnRes] = await Promise.all([taxApi.listRates(), taxApi.getTRN()]);
      setRates(ratesRes.data);
      setTrn(trnRes.data.trn || '');
      setTrnEdit(trnRes.data.trn || '');
    } catch {} finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ tax_name: '', percentage: '', tax_type: 'VAT', country: 'UAE', is_default: false });
    setError(''); setShowModal(true);
  };

  const openEdit = (rate) => {
    setEditingId(rate.id);
    setForm({ tax_name: rate.tax_name, percentage: String(rate.percentage), tax_type: rate.tax_type, country: rate.country, is_default: rate.is_default });
    setError(''); setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setSaving(true);
    try {
      const payload = { ...form, percentage: parseFloat(form.percentage) || 0 };
      if (editingId) {
        await taxApi.updateRate(editingId, payload);
      } else {
        await taxApi.createRate(payload);
      }
      setShowModal(false); loadData();
    } catch (err) { setError(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this tax rate?')) return;
    try { await taxApi.deleteRate(id); loadData(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleTrnSave = async () => {
    setTrnSaving(true); setTrnSuccess('');
    try {
      await taxApi.updateTRN(trnEdit);
      setTrn(trnEdit);
      setTrnSuccess('TRN updated successfully');
      setTimeout(() => setTrnSuccess(''), 3000);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to update TRN'); }
    finally { setTrnSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-5" data-testid="tax-settings-page">
      <div>
        <h1 className="text-2xl font-bold text-[#0A0A0A]">Tax Configuration</h1>
        <p className="text-sm text-[#666]">Manage VAT/GST rates and tax registration</p>
      </div>

      {/* TRN Section */}
      <Card className="p-4" data-testid="trn-section">
        <h2 className="text-sm font-semibold text-[#1A1F36] mb-3">Tax Registration Number (TRN)</h2>
        {trnSuccess && <div className="mb-2 bg-[#059669]/10 text-[#059669] px-3 py-1.5 rounded text-sm">{trnSuccess}</div>}
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <Input
              label="TRN"
              value={trnEdit}
              onChange={(e) => setTrnEdit(e.target.value)}
              placeholder="Enter Tax Registration Number"
              data-testid="trn-input"
            />
          </div>
          <Button onClick={handleTrnSave} disabled={trnSaving || trnEdit === trn} data-testid="save-trn-btn">
            {trnSaving ? 'Saving...' : 'Save TRN'}
          </Button>
        </div>
      </Card>

      {/* Tax Rates */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-[#1A1F36]">Tax Rates ({rates.length})</h2>
        <Button size="sm" onClick={openCreate} data-testid="add-tax-rate-btn"><Plus className="w-4 h-4 mr-1" /> Add Rate</Button>
      </div>

      {rates.length === 0 ? (
        <Card className="p-8 text-center text-[#666]" data-testid="no-rates">No tax rates configured</Card>
      ) : (
        <div className="space-y-2">
          {rates.map(rate => (
            <Card key={rate.id} className="p-3 sm:p-4" data-testid={`tax-rate-${rate.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-10 h-10 rounded-lg bg-[#DC2626]/10 flex items-center justify-center shrink-0">
                    <Percent className="w-5 h-5 text-[#DC2626]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{rate.tax_name}</span>
                      <Badge variant="primary">{rate.percentage}%</Badge>
                      <Badge variant={rate.is_active ? 'success' : 'default'}>{rate.is_active ? 'Active' : 'Inactive'}</Badge>
                      {rate.is_default && <Badge variant="warning">Default</Badge>}
                    </div>
                    <div className="flex gap-3 mt-0.5 text-xs text-[#888]">
                      <span>{rate.tax_type}</span>
                      <span>{rate.country}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => openEdit(rate)} className="p-1.5 text-[#666] hover:text-[#002FA7] hover:bg-[#F0F0F0] rounded" data-testid={`edit-rate-${rate.id}`}>
                    <PencilSimple className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(rate.id)} className="p-1.5 text-[#666] hover:text-[#FF3333] hover:bg-[#FEF2F2] rounded" data-testid={`delete-rate-${rate.id}`}>
                    <Trash className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingId ? 'Edit Tax Rate' : 'Create Tax Rate'}>
        <form onSubmit={handleSubmit} className="space-y-3" data-testid="tax-rate-form">
          {error && <div className="p-2 bg-[#FF3333]/10 text-[#FF3333] rounded text-sm">{error}</div>}
          <Input label="Tax Name" value={form.tax_name} onChange={(e) => setForm({ ...form, tax_name: e.target.value })} placeholder="e.g. VAT 5%" required data-testid="rate-name" />
          <Input label="Percentage (%)" type="number" min="0" max="100" step="0.01" value={form.percentage} onChange={(e) => setForm({ ...form, percentage: e.target.value })} required data-testid="rate-percentage" />
          <Select label="Tax Type" value={form.tax_type} onChange={(e) => setForm({ ...form, tax_type: e.target.value })} data-testid="rate-type">
            <option value="VAT">VAT</option>
            <option value="GST">GST</option>
          </Select>
          <Input label="Country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="rate-country" />
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="rate-default-toggle">
            <input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} className="rounded" />
            <span>Set as default tax rate</span>
          </label>
          <div className="flex justify-end gap-3 pt-3">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-rate-btn">{saving ? 'Saving...' : editingId ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
